import {
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  TooltipProvider,
} from '@monksflow/monks-ui';
import { PlugIcon, SearchIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganizationDisplayName } from '@/entities/user';
import { TABLE_COL_WIDTH } from '@/shared/constants';
import { EmptyState, SearchInput } from '@/shared/ui';
import { redesignTableStyles } from '@/shared/ui/data-table';
import { useConnectedApps, type ConnectedApp } from '../../model';
import { DisconnectAppDialog } from '../DisconnectAppDialog/DisconnectAppDialog';
import { ConnectedAppsRow } from './ConnectedAppsRow';
import { ConnectedAppsSkeletonRow } from './ConnectedAppsSkeletonRow';

const SKELETON_ROWS = Array.from({ length: 3 }, (_, index) => index);

export const ConnectedAppsTable = () => {
  const { t } = useTranslation('admin');
  const organizationName = useOrganizationDisplayName();
  const { data, isPending, isError } = useConnectedApps();
  const [pendingDisconnect, setPendingDisconnect] = useState<ConnectedApp | null>(null);
  const [search, setSearch] = useState('');

  if (isError) {
    return (
      <EmptyState
        role="alert"
        className="h-full"
        title={t('connectedApps.connectedAppsTable.errorTitle')}
        description={t('connectedApps.connectedAppsTable.errorDescription')}
      />
    );
  }

  const apps = data ?? [];

  if (!isPending && apps.length === 0) {
    return (
      <EmptyState
        icon={<PlugIcon />}
        className="h-full"
        title={t('connectedApps.connectedAppsTable.emptyTitle')}
        description={t('connectedApps.connectedAppsTable.emptyDescription', {
          organizationName,
        })}
      />
    );
  }

  const query = search.trim().toLowerCase();
  const visibleApps = query
    ? apps.filter((app) => app.clientName.toLowerCase().includes(query))
    : apps;

  return (
    <TooltipProvider>
      <div className="flex min-h-full flex-col gap-md">
        <SearchInput
          value={search}
          onChange={setSearch}
          aria-label={t('connectedApps.connectedAppsTable.searchLabel')}
          className="w-80"
        />

        {!isPending && visibleApps.length === 0 ? (
          <EmptyState
            icon={<SearchIcon />}
            className="flex-1"
            title={t('connectedApps.connectedAppsTable.noMatchesTitle')}
            description={t('connectedApps.connectedAppsTable.noMatchesDescription')}
          />
        ) : (
          <Datatable aria-busy={isPending || undefined} className={redesignTableStyles.table}>
            <DatatableHeader>
              <DatatableRow>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.person}>
                  {t('connectedApps.connectedAppsTable.name')}
                </DatatableHead>
                <DatatableHead scope="col">
                  {t('connectedApps.connectedAppsTable.access')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.lastActivity}>
                  {t('connectedApps.connectedAppsTable.connected')}
                </DatatableHead>
                <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions}>
                  <span className="sr-only">{t('connectedApps.connectedAppsTable.actions')}</span>
                </DatatableHead>
              </DatatableRow>
            </DatatableHeader>
            <DatatableBody>
              {isPending
                ? SKELETON_ROWS.map((index) => <ConnectedAppsSkeletonRow key={index} />)
                : visibleApps.map((app) => (
                    <ConnectedAppsRow key={app.id} app={app} onDisconnect={setPendingDisconnect} />
                  ))}
            </DatatableBody>
          </Datatable>
        )}
      </div>

      <DisconnectAppDialog
        app={pendingDisconnect}
        onClose={() => {
          setPendingDisconnect(null);
        }}
      />
    </TooltipProvider>
  );
};
